import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
    afterRenderEffect,
    ChangeDetectionStrategy,
    Component,
    effect,
    input,
    output,
    signal,
    viewChild
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TruncateTooltipDirective } from 'src/app/common/general/truncate-tooltip.directive';
import { QueueStatusLabelPipe } from '../../pipes/queue-status-label.pipe';
import { FormatRuntimePipe } from '../../pipes/format-runtime.pipe';
import { QueueStatusGroup } from '../../synchronization.model';
import { QueueRequestListItemDto, QueueRequestRow } from '../queue-request.model';
import { TaskBadgeComponent } from '../task-badge/task-badge.component';

/**
 * Fixed row height, in px. MUST match what the row actually renders — the virtual viewport positions
 * every row from this number, so a mismatch makes the scrollbar drift further out the longer the list
 * gets. That is also why the row must have no conditional lines: four text lines, always. Measured,
 * not derived: verify in the browser after any change to the row's typography or padding.
 */
const ROW_HEIGHT = 108;

/** How close to the bottom the viewport has to get before the next page is fetched. */
const LOAD_MORE_THRESHOLD_PX = 300;

/**
 * The scrolling kørsel list. Virtualised because "hent flere" is unbounded and a customer once queued
 * 50.000 requests in one batch.
 *
 * Presentational: it renders the rows it is handed and reports clicks and scrolled-to-bottom. It owns
 * no filter state and does no fetching.
 */
@Component({
    selector: 'app-queue-request-list',
    templateUrl: './queue-request-list.component.html',
    styleUrl: './queue-request-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CdkVirtualScrollViewport,
        CdkFixedSizeVirtualScroll,
        CdkVirtualForOf,
        DatePipe,
        NgbTooltip,
        FormatRuntimePipe,
        TaskBadgeComponent,
        TruncateTooltipDirective
    ]
})
export class QueueRequestListComponent {
    readonly rows = input.required<readonly QueueRequestRow[]>();
    readonly selectedId = input<string | null>(null);

    /** Scroll this row into view once, when it first appears. Used by the deep-link flow. */
    readonly focusId = input<string | null>(null);

    readonly loading = input(false);
    readonly loadingMore = input(false);
    readonly hasMore = input(false);
    readonly error = input<string | null>(null);
    readonly searchActive = input(false);

    readonly select = output<string>();
    readonly loadMore = output<void>();
    readonly retry = output<void>();

    protected readonly rowHeight = ROW_HEIGHT;

    private readonly viewport = viewChild(CdkVirtualScrollViewport);
    private readonly focused = signal<string | null>(null);

    constructor() {
        // The viewport only exists on the branch that renders rows, so re-subscribe whenever it comes
        // back. Teardown must go through `onCleanup`: an effect's return value is ignored, and
        // takeUntilDestroyed throws NG0203 at runtime here because an effect body is not an
        // injection context — it compiles perfectly happily either way.
        effect(onCleanup => {
            const viewport = this.viewport();
            if (!viewport) return;

            const subscription = viewport.elementScrolled().subscribe(() => {
                if (!this.hasMore() || this.loadingMore() || this.loading()) return;
                if (viewport.measureScrollOffset('bottom') < LOAD_MORE_THRESHOLD_PX) this.loadMore.emit();
            });

            onCleanup(() => subscription.unsubscribe());
        });

        // One-shot per id: without the guard this would keep re-scrolling and fight the user.
        afterRenderEffect(() => {
            const id = this.focusId();
            const viewport = this.viewport();
            if (!id || !viewport || this.focused() === id) return;

            const index = this.rows().findIndex(row => row.item.id === id);
            if (index < 0) return;

            viewport.scrollToIndex(index);
            this.focused.set(id);
        });
    }

    protected trackById(_index: number, row: QueueRequestRow): string {
        return row.item.id;
    }

    /**
     * Teaches the status-light colours on hover — the colour alone is not readable to a new user.
     *
     * Shows the precise status rather than the group: the four lines have no room for it as text,
     * and "Systemfejl" and "Fejlet" are the same colour but not the same problem. Falls back to the
     * group when the kørsel has no status row yet, which is a real state, not an error.
     */
    statusTooltip(item: QueueRequestListItemDto): string {
        return statusLabelPipe.transform(item.status) || groupLabel(item.statusGroup);
    }
}

/** Stateless and pure, so one shared instance is cheaper than a pipe binding on every row. */
const statusLabelPipe = new QueueStatusLabelPipe();

function groupLabel(group: Exclude<QueueStatusGroup, 'all'>): string {
    switch (group) {
        case 'running':
            return 'Kørende';
        case 'finished':
            return 'Færdig';
        case 'warning':
            return 'Advarsel';
        case 'failed':
            return 'Fejl';
    }
}
